/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Typography, InputLabel, Button, Divider, TextField, Checkbox, FormControlLabel, Link } from "@mui/material";
import { styled } from "@mui/material/styles";
import { CheckCircleOutline } from "@mui/icons-material";
import { AppContext } from "../../context/AppProvider";
import { Select, Item } from "../../components/Select";
import { HelpTooltip } from "../../components/Tooltip";

const SkuTooltipInfo = () => {
  const Header = styled(Typography)(({ theme }) => `
    text-align: center;
  `);

  const highlights = [
    "All Features in Professional",
  ];

  const renderFeatures = (features) => features.map((feature, index) =>
    <Stack key={index} direction="row" alignItems="center" spacing={2}>
      <CheckCircleOutline fontSize="small" color="action" />
      <Typography
        sx={{fontWeight: highlights.includes(feature) ? "bold" : "normal"}}
        variant="caption">{feature}</Typography>
    </Stack>
  );

  const starterFeatures = renderFeatures([
    "Browser Isolation",
    "Desktop as a Service",
    "Application Streaming",
    "Single Sign-On, SAML, MFA, LDAP",
    "Web Traffic Filtering",
    "SSH Key Management",
    "Customized Images",
    "Persistent Profiles and File Shares",
    "Self-Supported via Documentation",
  ]);

  return (
    <HelpTooltip sx={{ minWidth: "280px" }}>
      <Stack direction="column" spacing={3} px={2} py={2}>
        <Stack direction="row" spacing={1}>

          <Stack direction="column" spacing={2}>
            <Stack direction="column" m={2} spacing={2} justifyContent="center" >
              <Header variant="title">Starter Edition</Header>
              <Divider></Divider>
              <Stack direction="column" justifyContent="flex-begin" alignItems="flex-begin" spacing={1}>
                {starterFeatures}
              </Stack>
            </Stack>
          </Stack>

        </Stack>
      </Stack>
    </HelpTooltip>
  );
}

const LicenseTooltipInfo = () => {

  return (
    <HelpTooltip>
      <Stack direction="column" spacing={3} px={2} py={2}>
        <Typography variant="">License Type</Typography>
        <Typography variant="body" sx={{ textDecoration: "underline" }}>Per Named User:</Typography>
        <Typography variant="body2">
          License for each user registered within the database. Users may each have up to 5 concurrent sessions.
        </Typography>
        <Typography variant="body" sx={{ textDecoration: "underline" }}>Per Concurrent Session:</Typography>
        <Typography variant="body2">
          License for each concurrently active session at a single point in time. Unlimited users allowed within user database.
        </Typography>
      </Stack>
    </HelpTooltip>
  );
};

const VpnTooltipInfo = () => {

  return (
    <HelpTooltip>
      <Stack direction="column" spacing={3} px={2} py={2}>
        <Typography variant="">PureVPN Egress</Typography>
        <Typography variant="body2">
          We've partnered with PureVPN to automatically provide access to 80+ VPN gateways across the world without additional configuration required.
        </Typography>
        <Link href="https://kasmweb.com/docs/latest/guide/egress.html#purevpn-integration" variant="body2">
          Documentation
        </Link>
      </Stack>
    </HelpTooltip>
  );
};

export const ConfiguratorForm = () => {
  const { configuration, setLicense, setSKU, setSeats, setVpnSeats } = useContext(AppContext);

  const [optionVpn, setOptionVpn] = useState(false);


  const navigate = useNavigate();

  const goToCheckout = () => {
    navigate("/checkout");
  }

  const addVpn = (e) => {
    setOptionVpn(e.target.checked)
    if (e.target.checked) {
      setVpnSeats(configuration.seats)
    } else {
      setVpnSeats(0)
    }
  }

  return (
    <Stack direction="column" spacing={3} width="100%">
      <Select
        label={
          <Stack direction="row" spacing={1}>
            <InputLabel sx={{fontWeight: 'bold', color: '#000'}}>Version</InputLabel>
            <SkuTooltipInfo />
          </Stack>
        }
        value={configuration.sku}
        onChange={sku => setSKU(sku)}
      >
        <Item value={"starter"} label="Starter" />
      </Select>

      <Select
        label={
          <Stack direction="row" spacing={1}>
            <InputLabel sx={{fontWeight: 'bold', color: '#000'}}>License Type</InputLabel>
            <LicenseTooltipInfo />
          </Stack>
        }
        value={configuration.license}
        onChange={license => setLicense(license)}
      >
        <Item value={"user"} label="Per Named User" />
        <Item value={"concurrent"} label="Per Concurrent Session" />
      </Select>

      <Stack direction="column" spacing={2}>
        <InputLabel sx={{fontWeight: 'bold', color: '#000'}}>{configuration.license === "user" ? "Number of Users" : "Number of Sessions"}</InputLabel>
        <TextField type="number"
          value={configuration.seats}
          InputProps={{ inputProps: { min: 1, max: 25 } }}
          onChange={(input) => {
              let val = parseInt(input.target.value)
              if (isNaN(val)) {
                val = 1
              }
              if (val > 25) {
                val = 25
              }  
              setSeats(val)
            }
          }
          onFocus={(input) => input.target.select()}
          onBlur={(input) => {
            let val = parseInt(input.target.value)
            if (val < 1) {
              val = 1
            }
            if (val > 25) {
              val = 25
            }
            setSeats(val)
          }}
        />
      </Stack>

      <h3 className="checkout-options-h3">Optional Extras</h3>
      <div className="checkout-options">
          
      <Stack direction="column" spacing={2}>
          
          
          <FormControlLabel
            label={<div style={{ display: "flex", alignItems: "center" }}><InputLabel sx={{ fontWeight: 'bold', color: '#000', marginRight: '10px' }}>PureVPN Egress</InputLabel><VpnTooltipInfo /></div>}
            control={
              <Checkbox checked={optionVpn} onChange={(e) => addVpn(e) } />
            } />
        {optionVpn && 
        <TextField type="number"
          value={configuration.vpnSeats}
          defaultValue={configuration.seats}
          InputProps={{ inputProps: { min: 1, max: 25 } }}
          onChange={(input) => {
              let val = parseInt(input.target.value)
              if (isNaN(val)) {
                val = configuration.seats
              }
              if (val < configuration.seats) {
                val = configuration.seats
              }  
              setVpnSeats(val)
            }
          }
          onFocus={(input) => input.target.select()}
          onBlur={(input) => {
            let val = parseInt(input.target.value)
            if (val < configuration.seats) {
              val = configuration.seats
            }
            setVpnSeats(val)
          }}
        />
}
      </Stack>



      </div>

      <Stack alignItems="center">
        <Button
          style={{ width: '100%'}}
          variant="contained"
          size="large"
          color="primary"
          onClick={goToCheckout}
          sx={{ width: "30%", marginTop: '40px', padding: '15px' }}
        >Checkout</Button>
      </Stack>
    </Stack>
  );
}